import { uniqueId, noop } from 'lodash';
import classNames from 'classnames';
import { h } from 'preact';
import { useRef, useState } from 'preact/hooks';
import './button.scss';
import Spinner from '../spinner/spinner';

interface PureButtonProps {
	children: string | JSX.Element | JSX.Element[];
	isDisabled?: boolean;
	name?: string;
	className?: string;
	style?: { [key: string]: string | number };
	onClick?: (event?) => void;
	testId?: string;
	loading?: boolean;
	role?: string;
	ariaSelected?: boolean;
	ariaLabel?: string;
}

interface RippleProps {
	x: number;
	y: number;
	radius: number;
	onAnimationEnd: () => void;
}

interface Ripple {
	x: number;
	y: number;
	radius: number;
	key: string;
}

const getRippleFromEvent = (event, parent: HTMLElement): Ripple => {
	const { x: parentX, y: parentY } = parent.getBoundingClientRect();
	const x = event.clientX - parentX;
	const y = event.clientY - parentY;
	const width = parent.offsetWidth;
	const height = parent.offsetHeight;
	const radius = Math.max(width, height);
	return {
		x,
		y,
		radius,
		key: uniqueId()
	};
};

const Ripple = ({ x, y, radius, onAnimationEnd }: RippleProps) => {
	const style = {
		left: x - radius,
		top: y - radius,
		width: radius * 2,
		height: radius * 2
	};
	return <span className="ripple" style={style} onAnimationEnd={onAnimationEnd} />;
};

const Button = ({
	children,
	isDisabled,
	className,
	style,
	onClick: clientOnClick,
	name,
	testId,
	loading,
	role = 'button',
	ariaSelected,
	ariaLabel
}: PureButtonProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const [ripples, setRipples] = useState<Ripple[]>([]);

	const onClick = event => {
		const ripple = getRippleFromEvent(event, ref.current);
		setRipples([...ripples, ripple]);
		clientOnClick(event);
	};

	const clearRipple = () => {
		setRipples(ripples.slice(1));
	};

	const saveRef = (divRef: HTMLDivElement) => {
		ref.current = divRef;
	};

	return (
		<div
			ref={saveRef}
			className={classNames('pure-button', className, { disabled: isDisabled })}
			style={style}
			onClick={onClick}
			name={name}
			data-testid={testId}
			role={role}
			aria-selected={ariaSelected}
			aria-label={ariaLabel}
			aria-disabled={isDisabled}
			tabIndex={0}
		>
			<span className="text">
				{loading && <Spinner />}
				{children}
			</span>
			{ripples.map(ripple => (
				<Ripple {...ripple} onAnimationEnd={clearRipple} />
			))}
		</div>
	);
};

Button.defaultProps = {
	onClick: noop
};

export default Button;
